/** Stable identity key for a follow-up slot. */
export const slotKey = (opening: FollowUpOpening): string =>
  `${opening.startTime}|${opening.providerId}`;

/**
 * Returns the set of slot keys from `candidates` that overlap with any
 * slot in `selected`. Selected slots are excluded so they remain deselectable.
 *
 * The backend enforces a buffer after each appointment (`bufferInMinutes` from
 * `GET /appointment-types`). Two slots conflict when either would fall inside
 * the other's buffered window: `A.start < B.end + buffer && B.start < A.end + buffer`.
 * Boundary touch at the buffer edge is NOT a conflict.
 */
export const getOverlappingOpenings = (
  selected: FollowUpOpening[],
  candidates: FollowUpOpening[],
  bufferMinutes = 0,
): Set<string> => {
  const disabled = new Set<string>();
  if (!selected.length) return disabled;

  const bufferMs = bufferMinutes * 60_000;

  const selectedRanges = selected.map((s) => ({
    startMs: new Date(s.startTime).getTime(),
    endMs: new Date(s.endTime).getTime(),
    key: slotKey(s),
  }));

  for (const candidate of candidates) {
    const cKey = slotKey(candidate);
    if (selectedRanges.some((sr) => sr.key === cKey)) continue;

    const cStartMs = new Date(candidate.startTime).getTime();
    const cEndMs = new Date(candidate.endTime).getTime();

    if (
      selectedRanges.some(
        (sr) => cStartMs < sr.endMs + bufferMs && cEndMs + bufferMs > sr.startMs,
      )
    ) {
      disabled.add(cKey);
    }
  }

  return disabled;
};
